// src/services/Api.js
import axios from 'axios';
import { getAuthSession, notifyAuthSessionExpired, saveAuthTokens } from '../utils/AuthStorage';

// Cria uma instância do Axios
const api = axios.create({
  baseURL: window.APP_CONFIG?.Url_Api || 'http://default-url.com', // URL padrão
});

let refreshTokenPromise = null;
const refreshWindowInMs = 5 * 60 * 1000; // Renova 5 minutos antes do vencimento

const criarErroSessaoExpirada = () => {
  const error = new Error('Sua sessão expirou. Faça login novamente para continuar.');
  error.code = 'SESSION_EXPIRED';
  return error;
};

const dataValida = (value) => {
  if (!value) return null;
  const data = new Date(value);
  return Number.isNaN(data.getTime()) ? null : data;
};

const renovaToken = async (session) => {
  const baseURL = window.APP_CONFIG?.Url_Api || 'http://default-url.com';
  const accessTokenExpires = dataValida(session.tokenExpires);
  let response;

  if (session.refreshToken) {
    response = await axios.post(`${baseURL}/RefreshTokenV2`, {
      refreshToken: session.refreshToken,
    });
  } else if (session.token && accessTokenExpires && accessTokenExpires > new Date()) {
    // Compatibilidade para sessões abertas antes da publicação do refresh token V2.
    response = await axios.post(
      `${baseURL}/RefreshToken`,
      `"${session.token}"`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.token}`,
        },
      }
    );
  } else {
    throw criarErroSessaoExpirada();
  }

  saveAuthTokens(response.data?.token);
  return response.data.token.accessToken;
};

const obterTokenRenovado = (session) => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = renovaToken(session)
      .catch((error) => {
        notifyAuthSessionExpired();
        throw error;
      })
      .finally(() => {
        refreshTokenPromise = null;
      });
  }

  return refreshTokenPromise;
};

export const garantirSessaoAtiva = ({ forceRefresh = false } = {}) => {
  const session = getAuthSession();
  const tokenExpires = dataValida(session.tokenExpires);
  const refreshTokenExpires = dataValida(session.refreshTokenExpires);
  const now = new Date();

  if (!session.token || !tokenExpires) {
    notifyAuthSessionExpired();
    throw criarErroSessaoExpirada();
  }

  if (!forceRefresh && tokenExpires.getTime() - now.getTime() > refreshWindowInMs) {
    return session.token;
  }

  const podeUsarRefreshV2 = session.refreshToken && refreshTokenExpires && refreshTokenExpires > now;
  const podeUsarEndpointLegado = !session.refreshToken && tokenExpires > now;

  if (!podeUsarRefreshV2 && !podeUsarEndpointLegado) {
    notifyAuthSessionExpired();
    throw criarErroSessaoExpirada();
  }

  return obterTokenRenovado(session);
};

// Interceptores de requisição
api.interceptors.request.use(async (config) => {
  const token = await garantirSessaoAtiva();
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Interceptores de resposta
api.interceptors.response.use((response) => response, async (error) => {
  const originalRequest = error.config;

  if (error.response?.status === 401 && originalRequest && !originalRequest._retryAuth) {
    originalRequest._retryAuth = true;

    try {
      const token = await garantirSessaoAtiva({ forceRefresh: true });
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return api(originalRequest);
    } catch (_) {
      // A sinalização mantém a tela atual para não perder dados ainda não enviados.
    }
  }

  return Promise.reject(error);
});

const getApiErrorMessage = (data, error) => {
  if (error?.code === 'ERR_NETWORK' || (!error?.response && error?.request)) {
    return 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.';
  }

  if (typeof data === 'string' && data.trim()) {
    const primeiraLinha = data
      .split(/\r?\n/)
      .map((linha) => linha.trim())
      .find(Boolean);

    const mensagemDaExcecao = primeiraLinha?.match(
      /^(?:System\.)?[\w.]+Exception:\s*(.+)$/
    );

    return mensagemDaExcecao?.[1] || primeiraLinha;
  }

  if (data?.message) {
    return data.message;
  }

  if (data?.errors && typeof data.errors === 'object') {
    const mensagens = Object.entries(data.errors)
      .flatMap(([field, messages]) => {
        const list = Array.isArray(messages) ? messages : [messages];
        return list.map((message) => field === '$' ? message : `${field}: ${message}`);
      })
      .join('\n');

    if (mensagens) {
      return mensagens;
    }
  }

  if (data?.title) {
    return data.title;
  }

  const mensagensPorStatus = {
    400: 'Os dados informados são inválidos ou a operação não é permitida.',
    404: 'Registro não encontrado.',
    409: 'A operação entrou em conflito com outro registro ou processamento.',
    422: 'Os dados fiscais estão incompletos ou inválidos.',
    500: 'Ocorreu uma falha inesperada no servidor.',
    503: 'O serviço da SEFAZ está indisponível no momento.',
  };

  if (mensagensPorStatus[error?.response?.status]) {
    return mensagensPorStatus[error.response.status];
  }

  return error?.message || 'Erro na requisição';
};

const normalizeApiError = (error) => {
  const data = error?.response?.data;

  return {
    status: error?.response?.status,
    data,
    message: getApiErrorMessage(data, error),
    errors: data?.errors,
    originalError: error,
    toString() {
      return this.message;
    },
  };
};

// Função genérica para requisições GET
export const fetchData = async (endpoint, params = {}) => {
  try {
    const response = await api.get(endpoint, { params });
    return response.data;
  } catch (error) {
    console.error(`Erro ao obter dados de ${endpoint}:`, error);
    throw normalizeApiError(error);
  }
};

export const fetchBlobData = async (endpoint, params = {}) => {
  try {
    const response = await api.get(endpoint, { params, responseType: 'blob' });
    return response.data;
  } catch (error) {
    if (error?.response?.data instanceof Blob) {
      try {
        const texto = await error.response.data.text();
        error.response.data = JSON.parse(texto);
      } catch (_) {
        // Mantém o erro original quando o blob não contém JSON.
      }
    }
    console.error(`Erro ao obter blob de ${endpoint}:`, error);
    throw normalizeApiError(error);
  }
};

export const getFileNameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition) return '';

  const encodedMatch = contentDisposition.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  const plainMatch = contentDisposition.match(/filename\s*=\s*(?:"([^"]+)"|([^;]+))/i);
  const value = (encodedMatch?.[1] || plainMatch?.[1] || plainMatch?.[2] || '')
    .trim()
    .replace(/^"|"$/g, '');

  if (!value) return '';

  try {
    return decodeURIComponent(value);
  } catch (_) {
    return value;
  }
};

export const fetchFileData = async (endpoint, params = {}) => {
  try {
    const response = await api.get(endpoint, { params, responseType: 'blob' });
    if (response.status === 204 || !response.data || response.data.size === 0) {
      return null;
    }

    return {
      blob: response.data,
      nome: getFileNameFromContentDisposition(response.headers?.['content-disposition']),
      contentType: response.headers?.['content-type'] || response.data.type || 'application/octet-stream',
    };
  } catch (error) {
    if (error?.response?.data instanceof Blob) {
      try {
        const texto = await error.response.data.text();
        error.response.data = JSON.parse(texto);
      } catch (_) {
        // Mantém o erro original quando o blob não contém JSON.
      }
    }
    console.error(`Erro ao obter arquivo de ${endpoint}:`, error);
    throw normalizeApiError(error);
  }
};

// Função genérica para requisições POST
export const postData = async (endpoint, data) => {
  try {
    const response = await api.post(endpoint, data);
    return response.data;
  } catch (error) {
    console.error(`Erro ao enviar dados para ${endpoint}:`, error);
    throw normalizeApiError(error);
  }
};

// Função genérica para requisições PUT
export const putData = async (endpoint, data, config = {}) => {
  try {
    const response = await api.put(endpoint, data, config);
    return response.data;
  } catch (error) {
    console.error(`Erro ao atualizar dados em ${endpoint}:`, error);
    throw normalizeApiError(error);
  }
};

export const patchData = async (endpoint, data) => {
  try {
    const response = await api.patch(endpoint, data);
    return response.data;
  } catch (error) {
    console.error(`Erro ao atualizar dados em ${endpoint}:`, error);
    throw normalizeApiError(error);
  }
};

// Função genérica para requisições DELETE
export const deleteData = async (endpoint, params = {}, data = undefined) => {
  try {
    const response = await api.delete(endpoint, { params, data });
    return response.data;
  } catch (error) {
    console.error(`Erro ao excluir em ${endpoint}:`, error);
    throw normalizeApiError(error);
  }
};

// Exporta a instância do axios
export default api;
